import React from "react";
import "../Styling/Lab.css";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import Call from "../Components/Call";
import ChatbotWidget from "./ChatComponent";
import { motion, AnimatePresence } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  ArrowRight,
  Users,
  PhoneCall,
  FileText,
  Truck,
  BarChart3,
  Database,
  Layers,
  CheckCircle2,
  Sparkles,
  Zap
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const containerVariants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0,
      delayChildren: 0
    }
  }
};

const itemVariants = {
  hidden: { opacity: 1, y: 0 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0 }
  }
};

const featuredProjects = [
  {
    id: 1,
    title: "HR Operating System (HROS)",
    desc: "A unified HR operating system designed to move teams away from fragmented spreadsheets into a professional, connected environment.",
    longDesc: "DDW HROS is an enterprise-grade platform that centralizes your workforce management. From seamless onboarding to automated payroll integration, it eliminates the horizontal fragmentation caused by using multiple tools. Experience a unified source of truth for all employee data, performance tracking, and document management.",
    icon: <Users size={24} />,
    image: "/images/labs/hros_image1.jpeg",
    detailImage: "images/labs/hros_detail.jpeg",
    link: "/HRlandingpage",
    studioLink: "https://studio.digitaldreamworksagency.com/access",
    capabilities: ["Employee Lifecycle Management", "Automated Workflow Engine", "Centralized Data Security"],
    status: "Production Ready",
  },
  {
    id: 2,
    title: "AI Voice Assistant",
    desc: "A 24/7 AI front desk for local businesses that answers inbound calls, books appointments, and captures qualified leads automatically.",
    longDesc: "Our AI Voice Assistant provides a natural, human-like interface for your business 24/7. It doesn't just answer calls; it understands intent, handles complex scheduling, and integrates directly with your CRM. Eliminate missed revenue from peak-hour call overflows and provide instant satisfaction to every caller.",
    icon: <PhoneCall size={24} />,
    image: "images/labs/ai_voice_image1.jpeg",
    detailImage: "images/labs/ai_voice_detail.jpeg",
    link: "/Voicebotlandingpage",
    studioLink: "https://studio.digitaldreamworksagency.com/access",
    capabilities: ["Natural Language Logic", "Real-time Appointment Booking", "Multi-lingual Support"],
    status: "Live System",
  },
  {
    id: 3,
    title: "AI Proposal Engine",
    desc: "Generate consistent, professional proposals in minutes with dynamic pricing calculation and automated client data population.",
    longDesc: "Close deals faster with DDW's AI Proposal Engine. This system automates the tedious parts of sales—formatting, pricing calculations, and data entry. With proprietary intelligence layers, it drafts proposals that are not just professional but data-optimized for high conversion rates. Reduce sales cycle time by 60%.",
    icon: <FileText size={24} />,
    image: "images/labs/proposal_image1.jpeg",
    detailImage: "images/labs/proposal_detail.jpeg",
    link: "/Proposalbotlandingpage",
    studioLink: "https://studio.digitaldreamworksagency.com/proposalcard",
    capabilities: ["Dynamic Pricing Engine", "AI-optimized Templates", "Approval Workflow Automation"],
    status: "Production Ready",
  },
  {
    id: 4,
    title: "Truck Dispatching OS",
    desc: "Intelligent routing and coordination system for trucking operations, automating load assignment and fleet visibility.",
    longDesc: "Revolutionize your fleet management with our Truck Dispatching OS. It provides granular visibility into all operations, uses AI for route optimization, and automates communication between drivers and dispatchers. Built to handle complex logistics with ease, ensuring maximum fleet utilization and driver efficiency.",
    icon: <Truck size={24} />,
    image: "images/labs/truck_dispatch_image1.jpeg",
    detailImage: "images/labs/truck_dispatch_detail.jpeg",
    link: "/TruckDispatchinglandingpage",
    studioLink: "https://studio.digitaldreamworksagency.com/access",
    capabilities: ["Real-time Route Optimization", "Automated Load Assignment", "Fleet Intelligence Dashboard"],
    status: "Live System",
  },
];

const roadmapProjects = [
  {
    title: "Analytics Engine",
    desc: "Real-time multi-source analytics with AI-powered anomaly detection.",
    icon: <BarChart3 size={24} />
  },
  {
    title: "Data Pipeline",
    desc: "Enterprise-grade ETL and data orchestration for complex infra.",
    icon: <Database size={24} />
  },
  {
    title: "Content Orchestrator",
    desc: "Multi-channel content planning and scheduling system.",
    icon: <Layers size={24} />
  }
];

export default function LabScreen() {
  const canvasRef = React.useRef(null);
  const mainRef = React.useRef(null);
  const [selectedProject, setSelectedProject] = React.useState(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const openModal = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = "auto";
  };

  // Unified GSAP and Background Logic
  React.useEffect(() => {
    // --- Canvas Background Logic ---
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx_canvas = canvas.getContext("2d");
    let width, height, particles = [];

    const resize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    window.addEventListener("resize", resize);
    resize();

    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.8,
        vy: (Math.random() - 0.5) * 0.8,
        size: Math.random() * 8 + 3,
        color: Math.random() > 0.5 ? "rgba(51, 245, 13, 0.4)" : "rgba(0, 191, 255, 0.3)"
      });
    }

    const animate = () => {
      ctx_canvas.clearRect(0, 0, width, height);
      particles.forEach(p => {
        ctx_canvas.beginPath();
        ctx_canvas.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx_canvas.fillStyle = p.color;
        ctx_canvas.fill();
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
      });
      requestAnimationFrame(animate);
    };
    animate();

    // --- GSAP Animations ---
    const ctx_gsap = gsap.context((self) => {
      // Floating Animation for Cards
      gsap.to(".main-card", {
        y: -15,
        duration: 2.5,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

      gsap.to(".card-1", {
        y: 12,
        x: 8,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

      gsap.to(".card-2", {
        y: -12,
        x: -8,
        duration: 3.5,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

      // Ambient blobs movement - keeping these as they are ambient/background
      gsap.to(".Labs-blob", {
        x: "random(-100, 100)",
        y: "random(-100, 100)",
        duration: 10,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        stagger: 0.5
      });

      // Background mesh rotation
      gsap.to(".Labs-mesh-overlay", {
        rotation: 360,
        duration: 60,
        repeat: -1,
        ease: "none"
      });

      // Scroll reveal for cards - making it instant
      const sections = self.selector('.Labs-gsap-reveal');
      sections.forEach(section => {
        gsap.set(section, { opacity: 1, y: 0 });
      });

      // Force refresh on load/mount
      ScrollTrigger.refresh();
    }, mainRef);

    // Additional refresh after a short delay to account for image/layout shifts
    const refreshTimer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 500);

    return () => {
      window.removeEventListener("resize", resize);
      ctx_gsap.revert();
      clearTimeout(refreshTimer);
    };
  }, []);

  return (
    <div className="Labs-page-wrapper" ref={mainRef}>
      <Navbar />

      {/* Floating Particles Background */}
      <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.6 }} />

      {/* Cinematic Hero Section */}
      <section className="Labs-header-section">
        <div className="Labs-mesh-container">
          <div className="Labs-mesh-overlay"></div>
          <div className="Labs-blob blob-green"></div>
          <div className="Labs-blob blob-cyan"></div>
          <div className="Labs-blob blob-purple"></div>
        </div>

        <div className="container">
          <div className="Labs-hero-layout">
            <motion.div
              className="Labs-hero-content"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div variants={itemVariants} className="Labs-header-badge">
                <Sparkles size={16} /> Innovation Hub
              </motion.div>
              <motion.h1 variants={itemVariants} className="Labs-header-title">
                Deploying The <span className="green-gradient">Future Of Enterprise</span>
              </motion.h1>
              <motion.p variants={itemVariants} className="Labs-header-desc">
                Engineering proprietary software systems and AI operations. We build the infrastructure that powers serious operators.
              </motion.p>
              <motion.div variants={itemVariants} className="Labs-hero-ctas">
                <a
                  href="https://studio.digitaldreamworksagency.com/access"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="Labs-btn-hero"
                >
                  Request Lab Access <Zap size={20} />
                </a>
                <div className="Labs-trust">
                  <CheckCircle2 size={18} /> Internal R&D Unit
                </div>
              </motion.div>
            </motion.div>

            <div className="Labs-hero-visual">
              <div className="visual-card main-card">
                <Database size={40} />
                <span>R&D Engine</span>
              </div>
              <div className="visual-card secondary-card card-1">
                <Zap size={24} />
                <span>Scale</span>
              </div>
              <div className="visual-card secondary-card card-2">
                <Layers size={24} />
                <span>Full Stack</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="Labs-core-section">
        <div className="container">
          <div className="Labs-projects-grid">
            {featuredProjects.map((project) => (
              <div key={project.id} className="Labs-project-card Labs-gsap-reveal">
                <div className="Labs-card-image-box">
                  <img src={project.image} alt={project.title} className="Labs-card-image" />
                  <div className="Labs-card-overlay">
                    <div className="Labs-project-main-icon">{project.icon}</div>
                  </div>
                </div>

                <div className="Labs-card-content">
                  <div className="Labs-card-meta">
                    <span className="Labs-card-status">{project.status}</span>
                  </div>
                  <h3 className="Labs-card-title">{project.title}</h3>
                  <p className="Labs-card-desc">{project.desc}</p>

                  <div className="Labs-card-capabilities">
                    {project.capabilities.map((cap, index) => (
                      <div key={index} className="Labs-cap-item">
                        <CheckCircle2 size={14} className="Labs-check-icon" />
                        <span>{cap}</span>
                      </div>
                    ))}
                  </div>

                  <div className="Labs-card-footer">
                    <a
                      href={project.studioLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="Labs-btn-main"
                      onClick={() => {
                        if (window.fbq) {
                          window.fbq('trackCustom', 'RequestDemoOpened', {
                            source: 'labs_card_list',
                            page_title: project.title
                          });
                        }
                      }}
                    >
                      Request Demo <Zap size={18} />
                    </a>
                    <button
                      onClick={() => openModal(project)}
                      className="Labs-btn-ghost"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Detail Modal */}
      <AnimatePresence>
        {isModalOpen && selectedProject && (
          <motion.div
            className="Labs-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="Labs-modal-content"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="Labs-modal-close" onClick={closeModal}>×</button>

              <div className="Labs-modal-body">
                <div className="Labs-modal-image-side">
                  <img src={selectedProject.detailImage} alt={selectedProject.title} />
                </div>
                <div className="Labs-modal-info-side">
                  <div className="Labs-modal-badge">{selectedProject.status}</div>
                  <h2 className="Labs-modal-title">{selectedProject.title}</h2>
                  <p className="Labs-modal-description">{selectedProject.longDesc}</p>

                  <div className="Labs-modal-capabilities">
                    <h4>Core Capabilities</h4>
                    <div className="Labs-cap-list">
                      {selectedProject.capabilities.map((cap, i) => (
                        <div key={i} className="Labs-modal-cap-item">
                          <CheckCircle2 size={16} />
                          <span>{cap}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="Labs-modal-actions">
                    <a
                      href={selectedProject.studioLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="Labs-modal-btn-primary"
                      onClick={() => {
                        if (window.fbq) {
                          window.fbq('trackCustom', 'RequestDemoOpened', {
                            source: 'labs_modal',
                            page_title: selectedProject.title
                          });
                        }
                      }}
                    >
                      Request Demo <ArrowRight size={18} />
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Roadmap Section */}
      <section className="Labs-roadmap-section">
        <div className="container">
          <div className="Labs-section-header Labs-gsap-reveal">
            <span className="Labs-label-text">ROADMAP 2026</span>
            <h2>Current Research & Development</h2>
            <div className="Labs-title-underline" />
          </div>

          <div className="Labs-roadmap-grid">
            {roadmapProjects.map((project, index) => (
              <div key={index} className="Labs-roadmap-card Labs-gsap-reveal">
                <div className="Labs-roadmap-icon">{project.icon}</div>
                <h3>{project.title}</h3>
                <p>{project.desc}</p>
                <div className="Labs-roadmap-status">
                  <div className="Labs-status-dot"></div>
                  <span>INTERNAL R&D</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="LD-divider" />
      <ChatbotWidget />
      <Call />
      <Footer />
    </div>
  );
}
